import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, doc, onSnapshot, orderBy, query, limit, writeBatch, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import useEsMovil from '../hooks/useEsMovil';

const INFO_TIPO = {
  pregunta_ml: { icono: '❓', color: '#2E86AB', texto: 'Pregunta en Mercado Libre', enlaceTexto: 'Ver en Mercado Libre' },
  venta_ml: { icono: '🛒', color: '#2E7D32', texto: 'Venta en Mercado Libre', enlaceTexto: 'Ver en Mercado Libre' },
  reclamo_ml: { icono: '⚠️', color: '#E4212C', texto: 'Reclamo en Mercado Libre', enlaceTexto: 'Ver en Mercado Libre' },
  venta_tn_web: { icono: '🛍️', color: '#2E7D32', texto: 'Venta en Tienda Nube (web)', enlaceTexto: 'Ver en Mercado Libre' },
  stock_bajo: { icono: '📦', color: '#FF6B35', texto: 'Stock bajo', enlaceTexto: 'Ver producto' },
  ultima_unidad_ml: { icono: '🚫', color: '#E4212C', texto: 'Atención no vender, se vendió por Mercado Libre', enlaceTexto: 'Ver producto' },
  pago_proximo: { icono: '💸', color: '#FF6B35', texto: 'Pago próximo a vencer', enlaceTexto: 'Ver proveedores' },
  pago_vencido: { icono: '🚨', color: '#E4212C', texto: 'Pago vencido', enlaceTexto: 'Ver proveedores' },
  factura_duplicada: { icono: '🧾', color: '#E4212C', texto: 'Factura de proveedor repetida', enlaceTexto: 'Ver proveedores' },
  boleta_pendiente: { icono: '🧾', color: '#FF6B35', texto: 'Falta cargar una boleta', enlaceTexto: 'Cargar boleta' },
  pedido_por_hacer: { icono: '📝', color: '#2E86AB', texto: 'Hoy toca hacer un pedido', enlaceTexto: 'Ver pedidos' },
  pedido_por_llegar: { icono: '🚚', color: '#FF6B35', texto: 'Pedido debería haber llegado', enlaceTexto: 'Ver pedidos' },
  pdv_sin_cliente: { icono: '⭐', color: '#8E24AA', texto: 'Venta del PDV sin cliente', enlaceTexto: 'Ver clientes' },
  costo_aumento: { icono: '📈', color: '#E4212C', texto: 'Subió el costo de un producto', enlaceTexto: 'Ver producto' },
  cumpleanos: { icono: '🎂', color: '#2850A0', texto: 'Hoy cumple un cliente', enlaceTexto: 'Ver clientes' },
  devolucion_ml: { icono: '↩️', color: '#E4212C', texto: 'Devolución de Mercado Libre para confirmar', enlaceTexto: 'Ver devoluciones' },
};

// Dos campanitas separadas para que se distinga de un vistazo que tipo de
// aviso llegó: el camioncito es "algo paso con un cliente" (venta, pregunta,
// reclamo, en cualquiera de los dos canales), la campana es "algo necesita
// tu atencion interna" (stock, pagos a proveedores).
const TIPOS_VENTAS = new Set(['venta_ml', 'venta_tn_web', 'pregunta_ml', 'reclamo_ml']);
const TIPOS_ALARMA = new Set(['stock_bajo', 'ultima_unidad_ml', 'pago_proximo', 'pago_vencido', 'factura_duplicada', 'boleta_pendiente', 'pedido_por_hacer', 'pedido_por_llegar', 'pdv_sin_cliente', 'costo_aumento', 'cumpleanos', 'devolucion_ml']);

function formatearFecha(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

// Tres sonidos bien distintos (pedido de Mica, 2026-09-29: "se unifica todo
// y si no no sirve"), generados con Web Audio API sin archivos de audio:
//   venta        -- "¡bling!" de moneda
//   recordatorio -- "din-don" de timbre (hay que pasar un pedido, vence un pago...)
//   aviso        -- alarma grave (preguntas, reclamos, stock, facturas repetidas...)
const TIPOS_SONIDO_VENTA = new Set(['venta_ml', 'venta_tn_web']);
const TIPOS_SONIDO_STOCK = new Set(['stock_bajo', 'ultima_unidad_ml']);
const TIPOS_SONIDO_RECORDATORIO = new Set(['pedido_por_hacer', 'pedido_por_llegar', 'pago_proximo', 'pago_vencido', 'cumpleanos', 'boleta_pendiente']);

function tipoDeSonido(tipo) {
  if (TIPOS_SONIDO_VENTA.has(tipo)) return 'venta';
  if (TIPOS_SONIDO_STOCK.has(tipo)) return 'stock';
  if (TIPOS_SONIDO_RECORDATORIO.has(tipo)) return 'recordatorio';
  return 'aviso';
}

// Sonidos elegidos por Mica en Pixabay (2026-09-29), servidos desde
// public/sonidos/. Si un archivo falta o no se puede reproducir, suena el
// sonido generado de respaldo (ver reproducirSonido).
const ARCHIVO_SONIDO = {
  venta: '/sonidos/venta.mp3',
  stock: '/sonidos/stock-bajo.mp3',
  aviso: '/sonidos/notificacion.mp3',
};

function crearTocador(forma) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  if (ctx.state === 'suspended') ctx.resume();
  return (frecuencia, inicio, duracion, volumen) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = forma;
    osc.frequency.value = frecuencia;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + inicio);
    gain.gain.exponentialRampToValueAtTime(volumen, ctx.currentTime + inicio + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + inicio + duracion);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + inicio);
    osc.stop(ctx.currentTime + inicio + duracion + 0.05);
  };
}

// Venta: "¡bling!" de moneda de videojuego (dos notas cuadradas cortas,
// Si → Mi agudo). Mica pidió algo "bien de venta" (2026-09-29); el "iu-pi"
// que seguía después no le gustó y pidió dejar solo la moneda.
function sonidoVenta() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  if (ctx.state === 'suspended') ctx.resume();
  const t0 = ctx.currentTime;
  const nota = (forma, desde, hasta, inicio, duracion, volumen) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = forma;
    osc.frequency.setValueAtTime(desde, t0 + inicio);
    if (hasta !== desde) osc.frequency.exponentialRampToValueAtTime(hasta, t0 + inicio + duracion * 0.8);
    gain.gain.setValueAtTime(0.0001, t0 + inicio);
    gain.gain.exponentialRampToValueAtTime(volumen, t0 + inicio + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + inicio + duracion);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0 + inicio);
    osc.stop(t0 + inicio + duracion + 0.05);
  };
  nota('square', 987.8, 987.8, 0, 0.08, 0.22);
  nota('square', 1318.5, 1318.5, 0.08, 0.45, 0.22);
}

// Timbre de dos notas suaves (Mi → Do), largo, tipo "din-don".
function sonidoTimbre() {
  const tocar = crearTocador('sine');
  tocar(659.3, 0, 0.9, 0.7);
  tocar(1318.5, 0, 0.5, 0.15);
  tocar(523.3, 0.45, 1.2, 0.7);
  tocar(1046.5, 0.45, 0.6, 0.15);
}

// Alarma: dos tonos graves descendentes con onda áspera -- esto es un
// problema o algo a revisar, no una venta.
function sonidoAviso() {
  const tocar = crearTocador('sawtooth');
  tocar(220.0, 0, 0.28, 0.5);
  tocar(164.8, 0.22, 0.4, 0.5);
}

function sonidoGenerado(tipo) {
  try {
    if (tipo === 'venta') sonidoVenta();
    else if (tipo === 'recordatorio') sonidoTimbre();
    else sonidoAviso();
  } catch (_) {
    // el navegador bloqueó el audio (ej: todavía no hubo ninguna interacción
    // del usuario en la página) -- no rompe el resto de la app
  }
}

export function reproducirSonido(tipo) {
  const archivo = ARCHIVO_SONIDO[tipo];
  if (!archivo) { sonidoGenerado(tipo); return; }
  const audio = new Audio(archivo);
  audio.volume = 1;
  audio.play().catch((err) => {
    // Sin permiso para sonar todavía (el usuario no tocó la página): no
    // tiene sentido probar el de respaldo, también lo bloquearía.
    if (err?.name === 'NotAllowedError') return;
    sonidoGenerado(tipo);
  });
}

// Pedido de Mica (2026-10-17): "suenan a cada rato todos juntos". Dos causas:
// avisos que llegan en ráfaga (ej: 49 "subió el costo" al cargar una lista,
// uno cada 1,5 s, cada uno con su sonido) y la app abierta en varias pestañas,
// donde cada una sonaba por su cuenta. Ahora:
//  - lo que llega en ESPERA_AGRUPAR_MS se junta en una sola tanda;
//  - cada clase de sonido suena como mucho una vez cada PAUSA_MISMO_SONIDO_MS
//    entre TODAS las pestañas de la compu (navigator.locks + localStorage).
const ESPERA_AGRUPAR_MS = 3000;
const PAUSA_MISMO_SONIDO_MS = 30000;

function leerUltimoSonido(clase) {
  try { return Number(localStorage.getItem(`ultimo_sonido_${clase}`)) || 0; } catch (_) { return 0; }
}
function guardarUltimoSonido(clase, ms) {
  try { localStorage.setItem(`ultimo_sonido_${clase}`, String(ms)); } catch (_) { /* sin storage: solo vale el límite de esta pestaña */ }
}

const ultimoSonidoPestana = {};
function sonarTandaSinRepetir(clases) {
  const elegir = () => {
    const ahora = Date.now();
    const aSonar = clases.filter((c) => ahora - Math.max(leerUltimoSonido(c), ultimoSonidoPestana[c] || 0) >= PAUSA_MISMO_SONIDO_MS);
    aSonar.forEach((c) => { guardarUltimoSonido(c, ahora); ultimoSonidoPestana[c] = ahora; });
    aSonar.forEach((c, i) => setTimeout(() => reproducirSonido(c), i * 1400));
  };
  // El candado hace que dos pestañas no lean "no sonó todavía" al mismo tiempo.
  if (navigator.locks?.request) {
    navigator.locks.request('sonido-notificaciones', async () => elegir()).catch(elegir);
  } else {
    elegir();
  }
}

// Notificación nativa del navegador: aparece aunque estés en otra pestaña
// (mientras el navegador siga abierto). Necesita permiso del usuario.
function notificarSistemaOperativo(n, info) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(n.titulo || info.texto, { body: n.detalle || '', tag: n.id });
  } catch (_) {
    // algunos navegadores/SO pueden rechazar la notificación -- no rompe el resto de la app
  }
}

// Una campanita: recibe ya filtradas las notificaciones que le tocan (ventas
// o alarmas), asi cada una tiene su propio contador de "no leidas" y su
// propio desplegable, pero comparten el mismo listener de Firestore (ver
// CampanaNotificaciones mas abajo) para no duplicar lecturas.
function Campana({ icono, titulo, notificaciones, posicionDerecha, permisoNotificacion, pedirPermiso }) {
  const navigate = useNavigate();
  const esMovil = useEsMovil();
  const [abierta, setAbierta] = useState(false);
  const noLeidas = notificaciones.filter((n) => !n.leida);
  const tamañoBoton = esMovil ? 34 : 42;

  const marcarTodasLeidas = async () => {
    if (noLeidas.length === 0) return;
    const batch = writeBatch(db);
    noLeidas.forEach((n) => batch.update(doc(db, 'notificaciones', n.id), { leida: true }));
    await batch.commit();
  };

  const marcarUnaLeida = async (n) => {
    if (!n.leida) await updateDoc(doc(db, 'notificaciones', n.id), { leida: true });
  };

  return (
    <div style={{ position: 'fixed', top: esMovil ? '8px' : '14px', right: posicionDerecha, zIndex: 2000, fontFamily: 'Arial, sans-serif' }}>
      <button
        onClick={() => setAbierta((v) => !v)}
        style={{
          position: 'relative', width: `${tamañoBoton}px`, height: `${tamañoBoton}px`, borderRadius: '50%',
          border: 'none', background: '#1A1A2E', color: 'white', fontSize: esMovil ? '15px' : '18px',
          cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        }}
        title={titulo}
      >
        {icono}
        {noLeidas.length > 0 && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px', background: '#E4212C', color: 'white',
            borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', padding: '1px 6px', minWidth: '16px',
          }}>
            {noLeidas.length}
          </span>
        )}
      </button>

      {abierta && (
        <div style={esMovil ? {
          // En el celu va fija al ancho de la pantalla: anclada a la campana se
          // salía por la izquierda (la de ventas no está pegada al borde).
          position: 'fixed', top: `${tamañoBoton + 18}px`, left: '12px', right: '12px',
          maxHeight: 'calc(100vh - 80px)', overflowY: 'auto',
          background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        } : {
          position: 'absolute', top: `${tamañoBoton + 10}px`, right: 0,
          width: '340px', maxWidth: '340px', maxHeight: '440px', overflowY: 'auto',
          background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'space-between', alignItems: 'center' }}>
            <b style={{ fontSize: '14px', color: '#1A1A2E' }}>{titulo}</b>
            <button
              onClick={marcarTodasLeidas}
              disabled={noLeidas.length === 0}
              style={{ border: 'none', background: 'none', color: '#2E86AB', fontSize: '12px', cursor: noLeidas.length ? 'pointer' : 'default', opacity: noLeidas.length ? 1 : 0.5, minHeight: esMovil ? '36px' : undefined }}
            >
              Marcar todas como leídas
            </button>
          </div>

          {permisoNotificacion === 'default' && (
            <div style={{ padding: '10px 16px', background: '#FFF3E0', fontSize: '12px', color: '#9A5B00' }}>
              Activá los avisos para que suenen aunque estés en otra pestaña.
              <button
                onClick={pedirPermiso}
                style={{ display: 'block', marginTop: '6px', padding: esMovil ? '10px 14px' : '6px 12px', border: 'none', borderRadius: '6px', background: '#E4212C', color: 'white', fontSize: '12px', cursor: 'pointer' }}
              >
                Activar notificaciones
              </button>
            </div>
          )}
          {permisoNotificacion === 'denied' && (
            <div style={{ padding: '10px 16px', background: '#FFF3E0', fontSize: '12px', color: '#9A5B00' }}>
              Bloqueaste las notificaciones del navegador para este sitio. Para activarlas, entrá a la
              configuración del sitio (candado en la barra de direcciones) y permitilas a mano.
            </div>
          )}

          <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee', fontSize: '12px', color: '#666', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            Probar sonidos:
            {[['venta', '💰 Venta'], ['stock', '📦 Stock bajo'], ['recordatorio', '🔔 Recordatorio'], ['aviso', '💬 Otras']].map(([c, t]) => (
              <button key={c} onClick={() => reproducirSonido(c)} style={{ border: '1px solid #ddd', background: 'white', borderRadius: '6px', padding: esMovil ? '8px 10px' : '3px 8px', fontSize: '11px', cursor: 'pointer' }}>{t}</button>
            ))}
          </div>

          {notificaciones.length === 0 && (
            <p style={{ padding: '20px', color: '#999', fontSize: '13px', textAlign: 'center' }}>Sin notificaciones todavía.</p>
          )}

          {notificaciones.map((n) => {
            const info = INFO_TIPO[n.tipo] || { icono: '🔔', color: '#666', texto: n.tipo };
            return (
              <div
                key={n.id}
                onClick={() => {
                  marcarUnaLeida(n);
                  setAbierta(false);
                  if (!n.enlace) return;
                  if (n.enlace.startsWith('/')) {
                    navigate(n.enlace);
                  } else {
                    window.open(n.enlace, '_blank', 'noopener,noreferrer');
                  }
                }}
                style={{
                  padding: '12px 16px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                  background: n.leida ? 'white' : '#FFF8F0', display: 'flex', gap: '10px', alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: '18px' }}>{info.icono}</span>
                <div style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: n.leida ? 'normal' : 'bold', color: info.color }}>
                    {n.titulo || info.texto}
                  </p>
                  {n.detalle && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>{n.detalle}</p>}
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#999' }}>
                    {formatearFecha(n.timestamp)}
                    {n.enlace && <span style={{ color: '#2E86AB' }}> · {info.enlaceTexto || 'Ver más'} ↗</span>}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Quién ve qué (pedido de Mica, 2026-10-09):
//  - plata de proveedores (vencimientos, pagos, facturas repetidas) y avisos
//    de precios/costos: SOLO la administradora;
//  - pedidos a proveedores: la administradora y quien tenga el permiso
//    "Pedidos a proveedores" (hoy, solo Jesi).
const TIPOS_SOLO_ADMIN = new Set([
  'pago_proximo', 'pago_vencido', 'factura_duplicada', 'costo_aumento', 'precio_ml_actualizado', 'cuotas_ml_terminado',
]);
const TIPOS_PEDIDOS_PROVEEDOR = new Set(['pedido_por_hacer', 'pedido_por_llegar']);

function puedeVerNotificacion(n, perfil) {
  if (perfil?.rol === 'admin') return true;
  if (TIPOS_SOLO_ADMIN.has(n.tipo)) return false;
  if (TIPOS_PEDIDOS_PROVEEDOR.has(n.tipo)) return (perfil?.modulos || []).includes('pedidos_proveedor');
  return true;
}

function CampanaNotificaciones({ perfil }) {
  const [notificaciones, setNotificaciones] = useState([]);
  const [permisoNotificacion, setPermisoNotificacion] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const primeraCargaRef = useRef(true);
  const clasesPendientesRef = useRef(new Set());
  const timerSonidoRef = useRef(null);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(setPermisoNotificacion);
    }
  }, []);

  const pedirPermiso = () => {
    if (!('Notification' in window)) return;
    Notification.requestPermission().then(setPermisoNotificacion);
  };

  // El perfil llega después del primer render: se guarda en una ref para que
  // el filtro use siempre el actualizado, y se vuelve a suscribir al cambiar.
  const perfilRef = useRef(perfil);
  perfilRef.current = perfil;

  useEffect(() => {
    if (!perfil) return undefined;
    const q = query(collection(db, 'notificaciones'), orderBy('timestamp', 'desc'), limit(60));
    const unsub = onSnapshot(q, (snap) => {
      setNotificaciones(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((n) => puedeVerNotificacion(n, perfilRef.current)));

      if (!primeraCargaRef.current) {
        const nuevas = [];
        snap.docChanges().forEach((c) => {
          if (c.type !== 'added') return;
          const n = { id: c.doc.id, ...c.doc.data() };
          if (!puedeVerNotificacion(n, perfilRef.current)) return;
          nuevas.push(n);
          notificarSistemaOperativo(n, INFO_TIPO[n.tipo] || { texto: n.tipo });
        });
        // Se juntan los avisos de los próximos segundos y suena un sonido por
        // clase, uno después del otro (ver sonarTandaSinRepetir).
        nuevas.forEach((n) => clasesPendientesRef.current.add(tipoDeSonido(n.tipo)));
        if (clasesPendientesRef.current.size > 0 && !timerSonidoRef.current) {
          timerSonidoRef.current = setTimeout(() => {
            const clases = ['venta', 'stock', 'recordatorio', 'aviso'].filter((c) => clasesPendientesRef.current.has(c));
            clasesPendientesRef.current.clear();
            timerSonidoRef.current = null;
            sonarTandaSinRepetir(clases);
          }, ESPERA_AGRUPAR_MS);
        }
      }
      primeraCargaRef.current = false;
    });
    return () => {
      unsub();
      primeraCargaRef.current = true;
      clearTimeout(timerSonidoRef.current);
      timerSonidoRef.current = null;
      clasesPendientesRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfil?.rol, (perfil?.modulos || []).join(',')]);

  const notifVentas = notificaciones.filter((n) => TIPOS_VENTAS.has(n.tipo));
  const notifAlarmas = notificaciones.filter((n) => TIPOS_ALARMA.has(n.tipo));
  const esMovil = useEsMovil();

  return (
    <>
      <Campana
        icono="🚚"
        titulo="Ventas, preguntas y reclamos"
        notificaciones={notifVentas}
        posicionDerecha={esMovil ? '54px' : '72px'}
        permisoNotificacion={permisoNotificacion}
        pedirPermiso={pedirPermiso}
      />
      <Campana
        icono="🔔"
        titulo="Stock y pagos a proveedores"
        notificaciones={notifAlarmas}
        posicionDerecha={esMovil ? '12px' : '20px'}
        permisoNotificacion={permisoNotificacion}
        pedirPermiso={pedirPermiso}
      />
    </>
  );
}

export default CampanaNotificaciones;
